/**
 * GenerateProposalModal — the picker shown when a user takes the "Generate
 * proposal" next action on the post-RFC-approval Proposal step (Synrgy).
 *
 * It offers the two proposal types (Quick Proposal vs Informed Analysis); on
 * "Generate" the caller opens the account's Proposals tab in a new browser tab
 * with the chosen type preset and ready to run.
 */

import React from 'react';
import {FileText, Sparkles, Wand2} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {cn} from '@common-web/lib/utils';

export type ProposalGenerationType = 'QUICK_PROPOSAL' | 'INFORMED_ANALYSIS';

export interface GenerateProposalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyName: string;
  isPending?: boolean;
  onGenerate: (type: ProposalGenerationType) => void;
}

const OPTIONS: Array<{
  type: ProposalGenerationType;
  label: string;
  description: string;
  icon: typeof FileText;
}> = [
  {
    type: 'QUICK_PROPOSAL',
    label: 'Quick proposal',
    description: 'Generate a fast, high-level proposal to share with the group.',
    icon: FileText,
  },
  {
    type: 'INFORMED_ANALYSIS',
    label: 'Informed analysis',
    description: 'Build a detailed, census-backed analysis for a deeper presentation.',
    icon: Sparkles,
  },
];

export function GenerateProposalModal({
  open,
  onOpenChange,
  companyName,
  isPending = false,
  onGenerate,
}: GenerateProposalModalProps): React.ReactElement {
  const [selected, setSelected] = React.useState<ProposalGenerationType>('QUICK_PROPOSAL');

  // Reset to the default selection whenever the modal (re)opens.
  React.useEffect(() => {
    if (open) setSelected('QUICK_PROPOSAL');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Generate proposal</DialogTitle>
          <DialogDescription>
            Choose the kind of proposal to generate for{' '}
            <span className='font-semibold'>{companyName}</span>. The Proposals tab opens in a new
            tab with your selection ready.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-3 sm:grid-cols-2'>
          {OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isActive = selected === opt.type;
            return (
              <button
                key={opt.type}
                type='button'
                onClick={() => setSelected(opt.type)}
                aria-pressed={isActive}
                className={cn(
                  'flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors',
                  isActive
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50',
                )}
              >
                <Icon className={cn('h-5 w-5', isActive ? 'text-primary' : 'text-muted-foreground')} />
                <span className='text-sm font-semibold'>{opt.label}</span>
                <span className='text-xs text-muted-foreground'>{opt.description}</span>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => onGenerate(selected)} disabled={isPending}>
            <Wand2 className='mr-1.5 h-4 w-4' />
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
